import React from 'react'
import { useSelector, useDispatch } from 'react-redux'
import { useNavigate } from 'react-router-dom'
import ShipmentBarcodePage from '../ShipmentBarcodePage/ShipmentBarcodePageCanvas'
import {
  selectSort,
  setPage,
  setRowsPerPage,
  showPopup,
  getBarcodes,
  submitShipment,
  submitShipmentRequestSuccess,
} from '../../store/shipmentBarcode/actionCreator'
import CustomCanvasTable from 'components/CanvasTable/CustomCanvasTable'
import {
  Stepper,
  Card,
  Grid,
  Heading,
  Button,
} from '@enterprise-ui/canvas-ui-react'
import EnterpriseIcon, { PrinterIcon } from '@enterprise-ui/icons'

function getSteps() {
  return ['Print Barcode Labels', 'Shipping & Address', 'Confirmation']
}

function getNextButtonLabels() {
  return ['Next', 'Mark as Shipped', 'Done']
}

export default function VendorShipSamplesCanvas() {
  const shipmentBarcodeStore = useSelector((state) => state.shipmentBarcode)
  const dispatch = useDispatch()

  const [activeStep, setActiveStep] = React.useState(0)
  const [printButton, setPrintButton] = React.useState(false)
  const [provideDetailsLater, setProvideDetailsLater] = React.useState(false)
  const navigate = useNavigate()

  const steps = getSteps()

  const handleNext = () => {
    setActiveStep((prevActiveStep) => prevActiveStep + 1)
  }

  const handleBack = () => {
    setActiveStep((prevActiveStep) => prevActiveStep - 1)
  }

  const handleReset = () => {
    setActiveStep(0)
  }

  const onSortSelectedChange = (orderBy, order) => {
    dispatch(selectSort(orderBy, order))
  }

  const showPopupAction = (item) => {
    dispatch(showPopup(item))
  }

  const getBarcodesAction = (samples, showBarcodesCallback) => {
    dispatch(getBarcodes(samples, showBarcodesCallback))
  }

  const processShipmentAction = (data) => {
    dispatch(submitShipment(data, handleNext))
  }

  const submitShipmentRequestSuccessAction = () => {
    dispatch(submitShipmentRequestSuccess())
  }

  const handleGenerateBarcode = () => {
    const { data = [] } = shipmentBarcodeStore
    getBarcodesAction(data, (barcodes) => {
      const file = new Blob([barcodes], { type: 'application/pdf' })
      let fileURL
      if (window.navigator.msSaveOrOpenBlob) {
        fileURL = window.navigator.msSaveOrOpenBlob(file, 'barcodeLabel.pdf')
      } else {
        fileURL = URL.createObjectURL(file)
        window.open(fileURL).print()
      }
    })

    setPrintButton(true)
  }

  const handleProcessShipment = (event) => {
    const { data = [] } = shipmentBarcodeStore
    processShipmentAction(data)
  }

  const onCompleteProcess = async () => {
    submitShipmentRequestSuccessAction()
    handleReset()
    await Promise.resolve()
    navigate('/orderSummary')
  }

  const getStepControls = (stepIndex) => {
    const nextButtonLabels = getNextButtonLabels()
    switch (stepIndex) {
      case 0:
        return (
          <Grid.Item className="step-controls">
            <Button
              className="back-btn printer-btn"
              onClick={handleGenerateBarcode}
              type="secondary"
            >
              <EnterpriseIcon
                icon={PrinterIcon}
                style={{ marginRight: '5px' }}
              />
              Print New Labels
            </Button>
            <Button
              className="printer-btn"
              type="primary"
              disabled={!printButton}
              onClick={handleNext}
            >
              {nextButtonLabels[stepIndex]}
            </Button>
          </Grid.Item>
        )
      case 1:
        return (
          <Grid.Item className="step-controls">
            <Button
              className="back-btn printer-btn"
              disabled={activeStep === 0}
              onClick={handleBack}
            >
              Back
            </Button>
            <Button
              className="printer-btn"
              type="primary"
              onClick={handleProcessShipment}
              disabled={disableButton()}
            >
              {nextButtonLabels[stepIndex]}
            </Button>
          </Grid.Item>
        )
      case 2:
        return (
          <Grid.Item className="step-controls">
            <Button
              type="primary"
              disabled={!printButton}
              className="printer-btn"
              onClick={onCompleteProcess}
            >
              {nextButtonLabels[stepIndex]}
            </Button>
          </Grid.Item>
        )
      default:
        return 'Unknown stepIndex'
    }
  }

  const disableButton = () => {
    /**
     * Process Shipment Button should be enabled only if the following critera is met:
     * 1. printButton must always be triggered
     * 2. either tracking number and carrier are supplied or provideDetailsLater is checked
     *
     * returns true if button should be disabled, false otherwise
     */

    // returns boolean on true, if all tracking information fields are populated
    const { data = [] } = shipmentBarcodeStore
    let hasTrackingInfo = data.flatMap(
      (shipping) => !!shipping.trackingNumber && !!shipping.carrier && true
    )
    // ensure that either tracking info is fully provided or flag to defer information is selected [XOR operation used]
    let shipmentInfoUndefined = !hasTrackingInfo[0]
      ? !provideDetailsLater
      : !!provideDetailsLater

    return shipmentInfoUndefined
  }

  const enableMarkAsShipped = (provideDetailsLater = false) => {
    setProvideDetailsLater(provideDetailsLater)
  }

  const getStepContent = (stepIndex) => {
    const {
      stepOneColumnData = [],
      data = [],
      page = Number(0),
      rowsPerPage = 10,
      shipmentDestination,
    } = shipmentBarcodeStore

    switch (stepIndex) {
      case 0:
        return (
          <Grid.Container spacing="none">
            <Grid.Item xs={12}>
              <CustomCanvasTable
                columnData={stepOneColumnData}
                onSortSelectedChange={onSortSelectedChange}
                data={data}
                currentPage={page}
                rowsPerPage={rowsPerPage}
                totalRowsCount={data.length || Number(0)}
                showPopup={(data) => showPopupAction(data)}
                onRequestPage={setPage}
                onPageSizeChange={setRowsPerPage}
                sortable={true}
                paginationlocation="top-bottom"
              />
            </Grid.Item>
          </Grid.Container>
        )
      case 1:
        return <ShipmentBarcodePage parentCallback={enableMarkAsShipped} />
      case 2:
        return (
          <Grid.Container spacing="none">
            <Grid.Item xs={12}>
              <Heading className="shipped-message">
                {`${
                  'Shipment Successful! The following samples were marked as shipped to ' +
                  shipmentDestination
                }`}
              </Heading>
            </Grid.Item>
            <Grid.Item xs={12}>
              <CustomCanvasTable
                columnData={stepOneColumnData}
                onSortSelectedChange={onSortSelectedChange}
                data={data}
                currentPage={page}
                rowsPerPage={rowsPerPage}
                totalRowsCount={data.length || Number(0)}
                showPopup={(data) => showPopupAction(data)}
                onRequestPage={setPage}
                onPageSizeChange={setRowsPerPage}
                sortable={true}
                paginationlocation="top-bottom"
              />
            </Grid.Item>
          </Grid.Container>
        )
      default:
        return 'Unknown stepIndex'
    }
  }

  return (
    <Grid.Container
      className="vendor-ship-samples-container"
      align="center"
      direction="row"
      spacing="expanded"
    >
      <Grid.Item xs={12}>
        <Grid.Container spacing="none">
          <Card elevation={0} className="stepper-card">
            <Grid.Item xs={12}>
              <Grid.Container
                spacing="none"
                align={'center'}
                justify={'space-between'}
              >
                <Grid.Item>
                  <Heading className="title-header">Ship Samples</Heading>
                </Grid.Item>
                {getStepControls(activeStep)}
              </Grid.Container>
            </Grid.Item>
            <Grid.Item xs={12}>
              <Stepper
                activeStep={steps[activeStep]}
                style={{ padding: '24px' }}
              >
                {steps.map((label, ind) => (
                  <Stepper.Item
                    index={ind + 1}
                    name={label}
                    completed={ind < activeStep}
                  >
                    {label}
                  </Stepper.Item>
                ))}
              </Stepper>
            </Grid.Item>
          </Card>
        </Grid.Container>
      </Grid.Item>
      <Grid.Item xs={12}>
        <Grid.Container spacing="none">
          {activeStep === steps.length ? (
            <Grid.Item xs={12}>
              <p className="insturction-text ">All steps completed</p>
              <Button onClick={handleReset}>Reset</Button>
            </Grid.Item>
          ) : (
            <Grid.Item xs={12}>
              {getStepContent(activeStep, shipmentBarcodeStore)}
            </Grid.Item>
          )}
        </Grid.Container>
      </Grid.Item>
    </Grid.Container>
  )
}
